/**
 * Common site settings (formerly "widget")
 */
import React, { useContext, useEffect, useState } from "react";
import { Box, Typography, Stack, Grid, Divider } from "@mui/material";
import { AppContext } from "../context";
import { pathJoinUrl } from "@smfa/utils/src";
import { getWidget, addWidgetdata } from "../actions/widget.actions";
import RichTextEditor from "../components/RichTextEditor";
import FileUploadArea from "../components/common/FileUploadArea";
import FormActionBar from "../components/common/FormActionBar";
import LimitedTextField from "../components/common/LimitedTextField";
import ToolBody from "../components/common/ToolBody";

/**
 * @returns {JSX.Element}
 * @component
 */
const Site = () => {
  const { storage } = useContext(AppContext);

  const [loader, setLoader] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  var addWidget_default = {
    widget_section_id: 0,
    footer_logo: "",
    footer_logo_name: "",
    footer_logo_error: false,
    header_logo: "",
    header_logo_name: "",
    header_logo_error: false,
    footer_twitter_link: "",
    footer_twitter_link_maxlenth: 255,
    footer_twitter_link_error: false,
    footer_email_id: "",
    footer_email_id_maxlenth: 100,
    footer_email_id_error: false,
    footer_section_description: "", // Keep this for data model
    footer_section_description_maxlenth: 65535, // Keep this for data model
    footer_section_description_error: false,
  };
  // eslint-disable-next-line no-unused-vars
  const [loaderId, setLoaderId] = useState("");
  const [addWidget, setWidget] = useState(addWidget_default);

  const [deskimage, setDeskImage] = useState("");
  const onImageChange = (event) => {
    setSuccessMsg("");
    setErrorMsg("");

    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setDeskImage(URL.createObjectURL(file));
      setWidget((prev) => ({
        ...prev,
        footer_logo: file,
        footer_logo_name: file.name,
        footer_logo_error: false,
      }));
    }
  };

  const [mobimage, setMobImage] = useState("");
  const onMobImageChange = (event) => {
    setSuccessMsg("");
    setErrorMsg("");
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setMobImage(URL.createObjectURL(file));
      setWidget((prev) => ({
        ...prev,
        header_logo: file,
        header_logo_name: file.name,
        header_logo_error: false,
      }));
    }
  };

  const clearUpload = (field, clearPreview) => {
    setWidget((prev) => ({
      ...prev,
      [field]: "",
      [`${field}_name`]: "",
    }));
    clearPreview("");
  };

  // Handle RichTextEditor change for footer description
  const handleDescriptionChange = (event, editor) => {
    const data = editor.getData();
    setSuccessMsg("");
    setErrorMsg("");
    setWidget((prev) => ({
      ...prev,
      footer_section_description: data,
      footer_section_description_error: false,
    }));
  };

  useEffect(() => {
    getWidget().then((res) => {
      if (res.data.status === true) {
        const data = res.data.data;
        setWidget((prev) => ({
          ...prev,
          widget_section_id: data.widget_section_id || 0,
          footer_twitter_link: data.footer_twitter_link || "",
          footer_email_id: data.footer_email_id || "",
          footer_section_description: data.footer_section_description || "",
          footer_logo_name: data.footer_logo || "",
          header_logo_name: data.header_logo || "",
        }));
        if (data.footer_logo && storage[0]?.imageEndUrl) {
          setDeskImage(pathJoinUrl(storage[0].imageEndUrl, data.footer_logo));
        }
        if (data.header_logo && storage[0]?.imageEndUrl) {
          setMobImage(pathJoinUrl(storage[0].imageEndUrl, data.header_logo));
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addWidgetInfo = (event) => {
    setSuccessMsg("");
    setErrorMsg("");
    const { name, value } = event.target;

    setWidget((prev) => {
      const fieldMaxLength = prev[`${name}_maxlenth`];
      if (fieldMaxLength === undefined || value.length <= fieldMaxLength) {
        return {
          ...prev,
          [name]: value,
          [`${name}_error`]: false,
        };
      }
      return prev;
    });
  };

  const submitLocation = () => {
    setLoader(true);
    setSuccessMsg("");
    setErrorMsg("");

    let isValid = true;
    const newWidgetState = { ...addWidget };

    if (!addWidget.footer_twitter_link) {
      newWidgetState.footer_twitter_link_error = true;
      isValid = false;
    }
    if (!addWidget.footer_email_id) {
      newWidgetState.footer_email_id_error = true;
      isValid = false;
    }
    if (!addWidget.footer_section_description) {
      newWidgetState.footer_section_description_error = true;
      isValid = false;
    }
    // For new entries, validate image uploads (optional based on your logic)
    if (addWidget.widget_section_id === 0) {
      if (!addWidget.header_logo) {
        newWidgetState.header_logo_error = true;
        isValid = false;
        // setErrorMsg("Please upload Header Logo!"); // Example error message
      }
      if (!addWidget.footer_logo) {
        newWidgetState.footer_logo_error = true;
        isValid = false;
        // setErrorMsg("Please upload Footer Logo!"); // Example error message
      }
    }

    if (!isValid) {
      setWidget(newWidgetState);
      setLoader(false);
      setErrorMsg("Please fill all required fields."); // General error message
      return;
    }

    const formData = new FormData();
    formData.append("widget_section_id", addWidget.widget_section_id);
    if (addWidget.header_logo instanceof File) {
      formData.append("header_logo", addWidget.header_logo);
    } else if (addWidget.widget_section_id !== 0) {
      formData.append("header_logo_name", addWidget.header_logo_name);
    }
    if (addWidget.footer_logo instanceof File) {
      formData.append("footer_logo", addWidget.footer_logo);
    } else if (addWidget.widget_section_id !== 0) {
      formData.append("footer_logo_name", addWidget.footer_logo_name);
    }
    formData.append(
      "footer_section_description",
      addWidget.footer_section_description,
    );
    formData.append("footer_twitter_link", addWidget.footer_twitter_link);
    formData.append("footer_email_id", addWidget.footer_email_id);

    const isNew = addWidget.widget_section_id === 0;

    addWidgetdata(formData)
      .then((res1) => {
        if (res1.data.status === true) {
          setSuccessMsg(
            isNew
              ? "Widget Added Successfully!"
              : "Widget Updated Successfully!",
          );
          if (isNew) {
            // Optionally reset form or update ID from response
            // setWidget(addWidget_default); // Reset form
            // setDeskImage("");
            // setMobImage("");
            // If API returns the new ID, update it:
            // setWidget(prev => ({...prev, widget_section_id: res1.data.new_id}));
          }
          setTimeout(() => {
            setSuccessMsg("");
          }, 3000);
        } else {
          setErrorMsg(
            res1.data.message || "Something went wrong, please try again!",
          );
        }
      })
      .catch((error) => {
        setErrorMsg("Something went wrong, please try again!");
      })
      .finally(() => {
        setLoader(false);
      });
  };

  return (
    <ToolBody title="Site Settings">
      <Stack spacing={4} divider={<Divider />}>
        {/* Logos Section */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Logos
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FileUploadArea
                title="Header Logo"
                description={
                  <>
                    <span>Best logo Resolution: 150px X 137px</span>
                    <br />
                    <span>Supported files types: JPG, JPEG & PNG</span>
                  </>
                }
                previewUrl={mobimage}
                fileName={addWidget.header_logo_name}
                onChange={onMobImageChange}
                deleteRequest={{
                  resource: "widget",
                  id: addWidget.widget_section_id,
                  field: "header_logo",
                }}
                onRemove={() => clearUpload("header_logo", setMobImage)}
                accept=".png,.jpg,.jpeg"
                error={addWidget.header_logo_error}
                errorText="Please upload Header Logo!"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FileUploadArea
                title="Footer Logo"
                description={
                  <>
                    <span>Best logo Resolution: 150px X 137px</span>
                    <br />
                    <span>Supported files types: JPG, JPEG & PNG</span>
                  </>
                }
                previewUrl={deskimage}
                fileName={addWidget.footer_logo_name}
                onChange={onImageChange}
                deleteRequest={{
                  resource: "widget",
                  id: addWidget.widget_section_id,
                  field: "footer_logo",
                }}
                onRemove={() => clearUpload("footer_logo", setDeskImage)}
                accept=".png,.jpg,.jpeg"
                error={addWidget.footer_logo_error}
                errorText="Please upload Footer Logo!"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Contact Information Section */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
            Contact Information
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <em>
                Note: Social media links are now defined as static items. Please
                contact the developers to change them.
              </em>
              {/* <LimitedTextField
                  id="footer_twitter_link"
                  name="footer_twitter_link"
                  label="Twitter Link"
                  value={addWidget.footer_twitter_link}
                  onChange={addWidgetInfo}
                  maxLength={addWidget.footer_twitter_link_maxlenth}
                  error={addWidget.footer_twitter_link_error}
                  errorText="Please enter Twitter Link!"
                  fullWidth
                /> */}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <LimitedTextField
                id="footer_email_id"
                name="footer_email_id"
                label="Site Email"
                value={addWidget.footer_email_id}
                onChange={addWidgetInfo}
                maxLength={addWidget.footer_email_id_maxlenth}
                error={addWidget.footer_email_id_error}
                errorText="Please enter Site Email!"
                fullWidth
              />
            </Grid>
          </Grid>
        </Box>

        {/* Footer Description Section */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ mb: 1 }}>
            Footer Description
          </Typography>
          <Grid container spacing={3}>
            <Grid size={12}>
              <RichTextEditor
                data={addWidget.footer_section_description}
                onChange={handleDescriptionChange}
              />
              {addWidget.footer_section_description_error && (
                <Typography color="error" variant="caption">
                  Please enter Footer Description!
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Form Actions */}
        <FormActionBar
          onSubmit={submitLocation}
          isSubmitting={loader}
          isEdit={addWidget.widget_section_id !== 0}
          successMessage={successMsg}
          errorMessage={errorMsg}
          submitLabel={addWidget.widget_section_id === 0 ? "Add" : "Update"}
        />
      </Stack>
    </ToolBody>
  );
};
export default Site;
